import React, { useState } from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Text,
  Input,
  Card,
  tokens,
  Spinner
} from '@fluentui/react-components';
import { CheckmarkCircle24Regular, Dismiss24Regular } from '@fluentui/react-icons';
import { apiService } from '../services/api.service';

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  currentUsage?: number;
  maxAllowed?: number;
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({
  open,
  onClose,
  currentUsage = 0,
  maxAllowed = 5
}) => {
  const [licenseKey, setLicenseKey] = useState('');
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleActivateLicense = async () => {
    if (!licenseKey.trim()) {
      setError('Veuillez entrer une clé de licence');
      return;
    }

    setActivating(true);
    setError(null);

    try {
      const response = await (apiService as any).activateLicense(licenseKey);
      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          window.location.reload(); // Refresh to update UI with new plan
        }, 2000);
      } else {
        setError(response.error || 'Clé de licence invalide');
      }
    } catch (err) {
      setError('Erreur lors de l\'activation de la licence');
    } finally {
      setActivating(false);
    }
  };

  const handleMarketplaceClick = async () => {
    try {
      const response = await (apiService as any).getMarketplaceUrl();
      if (response.success && response.data?.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (err) {
      console.error('Failed to get marketplace URL:', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface style={{ maxWidth: '600px' }}>
        <DialogBody>
          <DialogTitle>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>🚀 Débloquez la création illimitée</span>
              <Button 
                appearance="subtle" 
                icon={<Dismiss24Regular />} 
                onClick={onClose}
              />
            </div>
          </DialogTitle>
          <DialogContent>
            {success ? (
              <div style={{ textAlign: 'center', padding: '32px' }}>
                <CheckmarkCircle24Regular style={{ fontSize: '48px', color: tokens.colorPaletteGreenForeground1 }} />
                <Text as="h3" style={{ marginTop: '16px' }}>Licence activée avec succès !</Text>
                <Text>Vous avez maintenant accès illimité.</Text>
              </div>
            ) : (
              <>
                <div style={{ 
                  backgroundColor: tokens.colorPaletteYellowBackground1,
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '24px'
                }}>
                  <Text weight="semibold">
                    Limite atteinte : {currentUsage}/{maxAllowed} demandes ce mois
                  </Text>
                  <Text as="p" size={200}>
                    Passez en Premium pour créer des équipes sans limite.
                  </Text>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <Card style={{ padding: '16px' }}>
                    <Text weight="semibold" as="h4">Plan Gratuit</Text>
                    <Text size={400} weight="bold" style={{ display: 'block', margin: '8px 0' }}>0€</Text>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      <li><Text size={200}>5 demandes/mois</Text></li>
                      <li><Text size={200}>Workflow standard</Text></li>
                      <li><Text size={200}>Support communautaire</Text></li>
                    </ul>
                  </Card>

                  <Card style={{ 
                    padding: '16px', 
                    border: `2px solid ${tokens.colorBrandBackground}`,
                    backgroundColor: tokens.colorBrandBackground2
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text weight="semibold" as="h4">Plan Premium</Text>
                      <Text size={100} style={{ 
                        backgroundColor: tokens.colorBrandBackground,
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '4px'
                      }}>
                        Recommandé
                      </Text>
                    </div>
                    <Text size={400} weight="bold" style={{ display: 'block', margin: '8px 0' }}>
                      99€<Text size={200}>/mois</Text>
                    </Text>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      <li><Text size={200}>✨ Demandes illimitées</Text></li>
                      <li><Text size={200}>Workflow standard</Text></li>
                      <li><Text size={200}>Support prioritaire</Text></li>
                      <li><Text size={200}>Analytiques d'usage</Text></li>
                    </ul>
                  </Card>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <Button 
                    appearance="primary" 
                    style={{ width: '100%', marginBottom: '16px' }}
                    onClick={handleMarketplaceClick}
                  >
                    Acheter via Microsoft Store
                  </Button>

                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '16px',
                    margin: '16px 0'
                  }}>
                    <div style={{ flex: 1, height: '1px', backgroundColor: tokens.colorNeutralStroke1 }} />
                    <Text size={200}>ou</Text>
                    <div style={{ flex: 1, height: '1px', backgroundColor: tokens.colorNeutralStroke1 }} />
                  </div>

                  <Text weight="semibold" style={{ display: 'block', marginBottom: '8px' }}>
                    Saisir une clé de licence
                  </Text>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Input
                      placeholder="XXXX-YYYY-ZZZZ-WWWW"
                      value={licenseKey}
                      onChange={(_, data) => setLicenseKey(data.value)}
                      style={{ flex: 1 }}
                    />
                    <Button 
                      appearance="secondary"
                      onClick={handleActivateLicense}
                      disabled={activating}
                    >
                      {activating ? <Spinner size="tiny" /> : 'Activer'}
                    </Button>
                  </div>
                  {error && (
                    <Text size={200} style={{ color: tokens.colorPaletteRedForeground1, marginTop: '8px' }}>
                      {error}
                    </Text>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
